import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import {
  getSchedulerConfig,
  approveSchedulerConfig,
} from "../../api/fees/feeSchedulerApi";
import { Button, Alert } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const FeeSchedulerApprovalNotification = ({ enabled = true }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [msg, setMsg] = useState("");

  const toast = useToast();

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await getSchedulerConfig();
      setCfg(res.data || res);
    } catch (err) {
      toast.error(err.response?.data?.message || "");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const approve = async () => {
    setActing(true);
    setMsg("");
    try {
      await approveSchedulerConfig({ enabled: true });
      setMsg("Fee scheduler approved and enabled.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approve failed");
    } finally {
      setActing(false);
    }
  };

  if (!enabled || loading) return null;

  if (!cfg || cfg.approvalStatus !== "PENDING_APPROVAL") {
    if (msg) {
      return (
        <div className="mb-4">
          <Alert variant="success" title="Scheduler Updated">
            {msg}
          </Alert>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mb-6 w-full">
      <Alert variant="info" className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 max-w-2xl">
            <h3 className="font-semibold text-info-900 text-sm">
              Fee scheduler approval
            </h3>
            <p className="text-xs text-info-800 leading-relaxed">
              A fee auto-scheduler config is waiting for your approval. Once approved, monthly
              batches will publish automatically on the configured day.
            </p>
            
            {msg && (
              <p className="mt-1 text-xs font-medium text-success-700">{msg}</p>
            )}
          </div>

          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 shrink-0">
            <Link to="/fees/scheduler" className="w-full sm:w-auto">
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
                className="w-full sm:w-auto text-info-900 hover:bg-info-100/50 justify-center"
              >
                Review
              </Button>
            </Link>

            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={acting}
              onClick={approve}
              leftIcon={<CheckCircle2 className="h-4 w-4" />}
              className="w-full sm:w-auto justify-center"
            >
              Approve & enable
            </Button>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default FeeSchedulerApprovalNotification;